import React, { useState } from 'react';
import {
  DataTitle,
  DataValue,
  IncompleteStateMarker,
  NoData,
  Poppins,
  Settings,
  Spacer,
  TitleWithBack,
} from 'src/common';
import CommonMenu from 'src/components/CommonMenu';
import styled from 'styled-components';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { useNavigate } from 'react-router-dom';
import {
  AssessmentModal,
  ConfirmDeleteModal,
  PdfExportModal,
  CopyAssessmentModal,
  ScenarioModal,
  VersionsPublishModal,
  ExecSummaryModal,
  RiskToleranceModalV2,
  VariablesWorkspaceModal,
  CommentsModal,
} from 'src/components/modals';
import { useScenario } from 'src/state/scenario';
import Versions from './Versions';
import { css } from 'styled-components';
import useVersion from 'src/hooks/useVersion';
import { VersionHeader } from 'src/hooks/useVersion/comps';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { isBefore } from 'date-fns';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { WizardIntent } from 'src/redux/reducers/app/types';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { useStateSelector } from 'src/redux';
import { formatDate } from 'src/utils/misc';
import { MDExportModal } from 'src/components/modals/MDExportModal';
import { useAuth } from 'src/state/auth';
import { useCache } from 'src/graphql';
import {
  AssessmentQuery,
  CompletionState,
  ObjectKind,
  useControlsRmpQuery,
  useDeleteAssessmentMutation,
  useAssessmentRiskRegisterLazyQuery,
} from 'src/graphql/_generated';
import QuickActionsMenu from 'src/components/QuickActionsMenu';
import { downloadCsv } from '../util';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { AiSuggestControl, AiSuggestScenario } from '.';
import InfoHint from 'src/components/InfoHint';
import { LABELS } from 'src/components/labels';

const Div = styled.div`
  position: relative;
  justify-content: space-between;
  display: flex;

  .heder {
    &__end {
      &__controls {
        display: flex;
        grid-gap: 20px;
        gap: 20px;
        align-items: center;
      }
    }
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;

  .mgroup {
    display: flex;
    align-items: center;
    grid-gap: 8px;
    &--20 {
      grid-gap: 20px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
  }
`;

const VersionArrow = styled.svg<{ $isOpen: boolean }>`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const Theader = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;

  .versions-controll {
    margin-left: 10px;
    height: 30px;
    width: 30px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .versions-controll {
      margin-left: 0px;
    }
  }
`;

type ModalId =
  | 'variables'
  | 'riskTolerance'
  | 'copyAssessment'
  | 'delete'
  | 'publish'
  | 'execSummary'
  | 'exportPdf'
  | 'versions'
  | 'assessment'
  | 'exportMd'
  | 'eventAction'
  | 'comments';

interface HeadProps {
  isLogarithmic: boolean;
  data: NonNullable<AssessmentQuery['assessment']>;
  expectedLossComponent: React.ReactNode;
}

export const Head: React.FC<HeadProps> = ({ isLogarithmic, data, expectedLossComponent }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const [modalId, setModalId] = useState<ModalId | null>(null);
  const { isOpen, closeModal } = useScenario();
  const { versionId, version } = useVersion();
  const { features } = useAuth();
  const { variables } = useWorkspaceVariables();
  const navigate = useNavigate();
  const { setWizardWorkspace } = useActionsApp();
  const assessmentId = data.id;

  const { refetchQuery } = useCache();
  const [fetchRiskRegister] = useAssessmentRiskRegisterLazyQuery();

  const hasScenariosWithoutControls = data.scenarios.some((el) => !el.riskManagementPlans.length);

  const [remove] = useDeleteAssessmentMutation({
    variables: {
      id: assessmentId,
    },
    onCompleted: async () => {
      mpEvent(MPEvents.AssessmentDelete, { id: assessmentId, tags: ['ASSESSMENT'] });
      await refetchQuery({ include: ['assessments'] });
      handleClose();
      navigate(`/`);
    },
  });

  const handleOnDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    remove();
  };

  const handleClose = () => setAnchorEl(null);

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  if (!data) return null;

  const hasIncompleteTech = data.scenarios.some(
    (el) => el.isIncomplete === CompletionState.Incomplete || el.isIncomplete === CompletionState.IncompleteEmptyVar,
  );

  const isReviewDue = isBefore(new Date(data.reviewDate || ''), new Date());
  const isSuggestionButtonReadOnly = !hasScenariosWithoutControls && !!data.scenarios.length;

  return (
    <>
      {modalId === 'comments' && (
        <CommentsModal
          onClose={() => setModalId(null)}
          subTitle={{ title: ObjectKind.Assessment, value: data.name }}
          eventData={{ objectType: ObjectKind.Assessment, objectId: assessmentId }}
          refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
        />
      )}
      {modalId === 'assessment' && <AssessmentModal id={data.id} onClose={() => setModalId(null)} />}
      {modalId === 'execSummary' && (
        <ExecSummaryModal
          assessmentId={assessmentId}
          executiveSummary={data.executiveSummary}
          onClose={() => setModalId(null)}
        />
      )}
      {isOpen && <ScenarioModal onClose={closeModal} invalidateQueries={['assessment', 'assessmentRiskRegister']} />}
      {modalId === 'variables' && (
        <VariablesWorkspaceModal
          scope={{ type: 'assessment', id: data.id, name: data.name }}
          onClose={() => setModalId(null)}
          readOnly={!!versionId}
        />
      )}
      {modalId === 'riskTolerance' && (
        <RiskToleranceModalV2 onClose={() => setModalId(null)} assessment={data} readOnly={!!versionId} />
      )}
      {modalId === 'copyAssessment' && (
        <CopyAssessmentModal onClose={() => setModalId(null)} assessmentId={assessmentId} />
      )}
      {modalId === 'delete' && <ConfirmDeleteModal onConfirm={handleOnDeleteClick} onClose={() => setModalId(null)} />}
      {modalId === 'exportPdf' && (
        <PdfExportModal
          onClose={() => setModalId(null)}
          isLogarithmic={isLogarithmic}
          assessmentId={assessmentId}
          scenarios={data.scenarios}
        />
      )}
      {modalId === 'publish' && <VersionsPublishModal onClose={() => setModalId(null)} assessmentId={assessmentId} />}
      <Div>
        {modalId === 'versions' && (
          <Versions assessmentId={assessmentId} versions={data.versions} onClose={() => setModalId(null)} top={120} />
        )}
        <TitleWithBack gradient title={'ASSESSMENT'} />
        <CommonMenu
          configuredList={[
            !versionId && {
              type: 'PUBLISH',
              exe: (e) => onMenuClick(e, () => setModalId('publish')),
              label: 'Publish a version',
            },
            !versionId && {
              type: 'EDIT',
              exe: (e) => onMenuClick(e, () => setModalId('assessment')),
            },
            !versionId && {
              type: 'DUPLICATE',
              exe: (e) => onMenuClick(e, () => setModalId('copyAssessment')),
            },
            !versionId && {
              type: 'DELETE',
              exe: (e) => onMenuClick(e, () => setModalId('delete')),
              divider: true,
            },
            !versionId && {
              type: 'WIZARD',
              exe: (e) =>
                onMenuClick(e, () =>
                  setWizardWorkspace({
                    intent: {
                      type: WizardIntent.scenario,
                      params: {
                        assessmentData: {
                          id: data.id,
                          name: data.name,
                          review_date: data.reviewDate,
                          description: data.description || '',
                          started_date: data.startedDate || undefined,
                        },
                      },
                    },
                  }),
                ),
              label: 'Scenario Wizard',
            },
            !versionId && {
              type: 'PDF',
              exe: (e) => onMenuClick(e, () => setModalId('exportPdf')),
            },
            !versionId &&
              features['app.export.md'] && {
                type: 'MD',
                exe: (e) => onMenuClick(e, () => setModalId('exportMd')),
              },
            !versionId && {
              type: 'CSV',
              exe: (e) =>
                onMenuClick(e, async () => {
                  const { data: rrData } = await fetchRiskRegister({
                    variables: { id: assessmentId, versionId },
                  });
                  downloadCsv({
                    assessment: { id: data.id, name: data.name },
                    scenarios: rrData?.assessment?.scenarios || [],
                    assessmentVariables: data.variables,
                    workspaceVariables: variables,
                    onDone: () => queryCache.invalidateQueries([QKeys.Audit]),
                  });
                }),
            },
          ]}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        />
        {!versionId && (
          <div className="heder__end__controls">
            <QuickActionsMenu
              actions={[
                {
                  type: 'SNAPSHOTS',
                  disabled: !!versionId,
                  onClick: () => {
                    setModalId('versions');
                    mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Snapshots', tags: ['ASSESSMENT'] });
                  },
                },
                {
                  type: 'ASSESSMENT_VARIABLES',
                  disabled: !!versionId,
                  onClick: () => {
                    setModalId('variables');
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'QuickActions-Assessment-Variables',
                      tags: ['ASSESSMENT'],
                    });
                  },
                },
                {
                  type: 'EVENT_ACTION',
                  disabled: !features['app.events'] || !!versionId,
                  onClick: () => {
                    setModalId('comments');
                    mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Comments', tags: ['ASSESSMENT'] });
                  },
                },
                {
                  type: 'RISK_TOLERANCE',
                  disabled: !!versionId,
                  onClick: () => {
                    setModalId('riskTolerance');
                    mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Risk-Tolerance', tags: ['ASSESSMENT'] });
                  },
                },
                {
                  type: 'SUGGESTIONS',
                  onClick: () =>
                    mpEvent(MPEvents.ButtonClick, { button: 'QuickActions-Suggestions', tags: ['ASSESSMENT', 'AI'] }),
                  itemProps: {
                    suggesterKeys: [`${assessmentId}-control`, `${assessmentId}-scenario`],
                    type: 'ASSESSMENT',
                  },
                  disabled: !features['app.ai.suggest-controls-v2'],
                  readonly: isSuggestionButtonReadOnly,
                },
              ]}
            />
            <Settings
              active={!!anchorEl}
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
              data-cy="common-menu"
            />
          </div>
        )}
      </Div>
      <Spacer $px={30} />
      <Theader>
        <Poppins px={32} weight={600} data-cy="assessment-name">
          {data.name}
        </Poppins>
        {hasIncompleteTech && (
          <div css="margin-left: 8px;">
            <IncompleteStateMarker
              initState="TECH"
              titleTech="One or more scenarios are missing calculation data and have been excluded from the calculations."
              place="right"
            />
          </div>
        )}
        <div
          className="versions-controll"
          onClick={() => {
            const isVersionsModalActive = modalId === 'versions';
            setModalId(isVersionsModalActive ? null : 'versions');
            mpEvent(MPEvents.ButtonClick, {
              button: 'Versions',
              value: isVersionsModalActive ? 'Close' : 'Open',
              tags: ['ASSESSMENT'],
            });
          }}
          data-cy="versions-open-switch"
        >
          <VersionArrow
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="6"
            viewBox="0 0 11 6"
            fill="none"
            $isOpen={modalId === 'versions'}
          >
            <path
              d="M9.88629 0H0.659141C0.480523 0 0.326108 0.0652146 0.195643 0.195535C0.0651783 0.326 0 0.480415 0 0.658925C0 0.837435 0.0651783 0.991959 0.195643 1.12235L4.80923 5.73587C4.93984 5.86634 5.09426 5.93166 5.27273 5.93166C5.45121 5.93166 5.60577 5.86634 5.73612 5.73587L10.3497 1.12232C10.48 0.991958 10.5455 0.837435 10.5455 0.658889C10.5455 0.480415 10.4801 0.326 10.3497 0.195499C10.2194 0.06507 10.0648 0 9.88629 0Z"
              fill={colors.cflowerBlue}
            />
          </VersionArrow>
        </div>
      </Theader>
      {version && (
        <>
          <Spacer $px={5} />
          <VersionHeader version={version} />
        </>
      )}
      {features['app.ai.suggest-controls-v2'] && data && (
        <>
          <Spacer $px={10} />
          <div css="display: flex; flex-direction: column; gap: 10px;">
            {!!data.scenarios.length && hasScenariosWithoutControls && <AiSuggestControl assessment={data} />}
            {!data.scenarios.length && <AiSuggestScenario assessment={data} />}
          </div>
        </>
      )}
      <Spacer $px={20} />
      <Meta>
        <div className="mgroup mgroup--20">
          <div className="mgroup">
            <DataTitle $noMargin>START DATE:</DataTitle>
            {data.startedDate ? (
              <DataValue data-cy="assessment-started_date">
                <TLLink date={data.startedDate}>{formatDate(data.startedDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div className="mgroup">
            <DataTitle $noMargin>REVIEW DUE:</DataTitle>
            {data.reviewDate ? (
              <DataValue data-cy="assessment-date" data-highlighted={isReviewDue}>
                <span style={{ color: isReviewDue ? colors.error : colors.prussianBlue }}>
                  <TLLink date={data.reviewDate}>{formatDate(data.reviewDate)}</TLLink>
                </span>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>

        <div className="mgroup">
          <DataTitle $noMargin>
            <InfoHint tooltipText={LABELS.expectedLoss} noIcon>
              EXPECTED LOSS:
            </InfoHint>
          </DataTitle>
          {expectedLossComponent}
        </div>
      </Meta>
    </>
  );
};
